import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

export type ToastKind = '' | 'good' | 'bad' | 'warn';

interface ToastContextValue {
  toast: (message: string, kind?: ToastKind) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/*
 * Toasts show at the bottom right (see .toast in public/styles.css); on phones
 * they sit across the bottom, above the menu dock. Each carries an icon for its
 * kind and stays long enough to read a full sentence.
 */
const ICON: Record<ToastKind, string> = {
  '': 'ri-information-line',
  good: 'ri-checkbox-circle-line',
  bad: 'ri-error-warning-line',
  warn: 'ri-alert-line',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState('');
  const [kind, setKind] = useState<ToastKind>('');
  const [visible, setVisible] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const toast = useCallback((msg: string, k: ToastKind = '') => {
    window.clearTimeout(timer.current);
    setMessage(msg);
    setKind(k);
    setVisible(true);
    timer.current = window.setTimeout(() => setVisible(false), 4500);
  }, []);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className={`toast ${visible ? 'show' : ''} ${kind}`}
        role="status"
        aria-live="polite"
      >
        <i className={ICON[kind]} aria-hidden="true" />
        <span>{message}</span>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx.toast;
}
